import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, MapPin, Package } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Item } from '@/app/types/item';
import { getItemSiteAvailability } from '@/app/services/itemAvailabilityService';
import { listSellableUnits } from '@/app/services/sellableUnitService';
import { SiteAvailabilityModal } from './SiteAvailabilityModal';
import { SellableUnitsModal } from './SellableUnitsModal';

interface MobileTabProps {
  item: Item;
}

export function MobileTab({ item }: MobileTabProps) {
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [sellableUnitsModalOpen, setSellableUnitsModalOpen] = useState(false);

  // Fetch site availability data
  const { data: availabilityData, isLoading: isLoadingAvailability } = useQuery({
    queryKey: ['item-site-availability', item?.id],
    queryFn: () => getItemSiteAvailability(item.id),
    enabled: !!item?.id,
  });

  // Fetch sellable units count (only for display)
  const { data: sellableUnits, isLoading: isLoadingUnits } = useQuery({
    queryKey: ['sellable-units-count', item?.id],
    queryFn: () => listSellableUnits(item.id, { is_active_only: true }),
    enabled: !!item?.id,
  });

  if (item?.item_type !== 'product') {
    return (
      <Alert>
        <AlertDescription>
          Mobile features are only available for product items.
        </AlertDescription>
      </Alert>
    );
  }

  const availableSitesCount = availabilityData?.filter(a => a.is_available).length || 0;
  const totalSitesCount = availabilityData?.length || 0;
  const activeSellableUnitsCount = sellableUnits?.length || 0;

  return (
    <div className="space-y-6">
      {/* Site Availability Section */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium">Site Availability</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Control which sites this item is available for sale and purchase
            </p>
          </div>
          <Button
            onClick={() => setSiteModalOpen(true)}
            variant="outline"
          >
            <MapPin className="h-4 w-4 mr-2" />
            Manage Sites
          </Button>
        </div>

        {/* Site Availability Status Card */}
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Current Availability Status</Label>
                <div className="mt-2 flex items-center gap-4">
                  <Badge variant={availableSitesCount > 0 ? 'default' : 'secondary'}>
                    {availableSitesCount} of {totalSitesCount} Sites Active
                  </Badge>
                  {availableSitesCount === 0 && (
                    <span className="text-sm text-warning">
                      ⚠️ Item is not available at any site
                    </span>
                  )}
                </div>
              </div>

              {isLoadingAvailability ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Available Sites:</Label>
                  <div className="flex flex-wrap gap-2">
                    {availabilityData?.filter(a => a.is_available).length === 0 ? (
                      <span className="text-sm text-muted-foreground">No sites selected</span>
                    ) : (
                      availabilityData
                        ?.filter(a => a.is_available)
                        .map(availability => (
                          <Badge key={availability.site_id} variant="outline">
                            {availability.site?.name || `Site ${availability.site_id}`}
                          </Badge>
                        ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Sellable Units Section */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium">Sellable Units</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Pre-defined purchase quantities for mobile applications
            </p>
          </div>
          <Button
            onClick={() => setSellableUnitsModalOpen(true)}
            variant="outline"
          >
            <Package className="h-4 w-4 mr-2" />
            Manage Units
          </Button>
        </div>

        {/* Sellable Units Status Card */}
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Configured Units</Label>
                <div className="mt-2 flex items-center gap-4">
                  {isLoadingUnits ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Badge variant={activeSellableUnitsCount > 0 ? 'default' : 'secondary'}>
                        {activeSellableUnitsCount} Active {activeSellableUnitsCount === 1 ? 'Unit' : 'Units'}
                      </Badge>
                      {activeSellableUnitsCount === 0 && (
                        <span className="text-sm text-muted-foreground">
                          No sellable units configured
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>

              {!isLoadingUnits && sellableUnits && sellableUnits.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Active Units:</Label>
                  <div className="flex flex-wrap gap-2">
                    {sellableUnits.map(unit => (
                      <Badge key={unit.id} variant="outline">
                        {unit.display_name} ({parseFloat(unit.quantity_multiplier).toFixed(2)} {item.base_unit_name})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <Alert>
                <AlertDescription className="text-sm">
                  <strong>Mobile App Integration:</strong> Sellable units allow customers to select 
                  pre-defined quantities like "200g Pouch" or "Family Pack" in mobile apps. 
                  Prices are automatically calculated as base price × quantity multiplier.
                </AlertDescription>
              </Alert>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      {item && (
        <>
          <SiteAvailabilityModal
            open={siteModalOpen}
            onOpenChange={setSiteModalOpen}
            item={item}
          />
          <SellableUnitsModal
            open={sellableUnitsModalOpen}
            onOpenChange={setSellableUnitsModalOpen}
            item={item}
          />
        </>
      )}
    </div>
  );
}